import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Film, Loader2 } from 'lucide-react';
import { downloadUpload } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const statusStyle = (s) => ({
  complete: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  failed: 'bg-red-500/20 text-red-400'
}[s] || 'bg-yellow-500/20 text-yellow-400');

const UploadRow = ({ upload, token }) => {
  const status = upload.processing_status || upload.status;
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-3 p-3 bg-zinc-800/50 border border-zinc-800" data-testid={`user-upload-row-${upload.id}`}>
      <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
        <p className="text-sm text-white truncate" title={upload.filename}>{upload.filename}</p>
        <p className="text-xs text-zinc-500">
          {formatBytes(upload.size_bytes)} • {new Date(upload.completed_at || upload.created_at).toLocaleString()}
        </p>
      </div>
      <span className={`px-2 py-0.5 text-xs uppercase rounded shrink-0 ${statusStyle(status)}`} data-testid={`user-upload-status-${upload.id}`}>
        {status === 'complete' ? 'Ready' : status}
      </span>
      {upload.file_exists ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => downloadUpload(upload.id, upload.filename, token)}
          className="h-7 px-2 shrink-0 text-zinc-400 hover:text-green-400 hover:bg-green-500/10"
          data-testid={`user-upload-download-btn-${upload.id}`}
        >
          <Download className="w-3.5 h-3.5 mr-1" />
          <span className="text-xs">Download</span>
        </Button>
      ) : (
        <span className="text-xs text-zinc-600 shrink-0 whitespace-nowrap" data-testid={`user-upload-missing-${upload.id}`}>Not on NAS</span>
      )}
    </div>
  );
};

export const UserUploadsDialog = ({ user, token, onClose }) => {
  const [uploads, setUploads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    axios.get(`${API}/admin/uploads?user_id=${user.id}&limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUploads(res.data))
      .catch(() => setUploads([]))
      .finally(() => setIsLoading(false));
  }, [user, token]);

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl w-[calc(100vw-2rem)] sm:w-full" data-testid="user-uploads-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {user?.username?.toUpperCase()} — UPLOADS
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : uploads.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-zinc-500" data-testid="user-uploads-empty">
            <Film className="w-8 h-8 mb-2" />
            <p className="text-sm">No uploads yet</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto sm:pr-3">
            <div className="space-y-2" data-testid="user-uploads-list">
              {uploads.map((u) => <UploadRow key={u.id} upload={u} token={token} />)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
